/**
 * Helpers de filtrado de distancias de carrera por nivel del atleta.
 *
 * v15.5-§8.4 (brief Alberto post-beta): los atletas de nivel "beginner"
 * solo pueden seleccionar carreras objetivo de hasta 5K. Si quieren
 * competir en distancias mayores, deben cambiar su nivel a "amateur".
 *
 * Este helper aplica el FILTRO en el frontend para que el dropdown del
 * usuario NO ofrezca distancias bloqueadas. La defensa backend está en
 * `backend/services/training_engine/race_validation.py` — si el frontend
 * está desincronizado o se manipula el request, el backend devuelve 400
 * con código `beginner_max_5k`.
 *
 * Convención de nivel: API/BD usa `"beginner"`/`"amateur"` (English).
 * El motor interno usa `"principiante"`/`"amateur"` (Spanish). Este
 * helper vive en la capa frontend → recibe los valores English directos.
 *
 * Mantener SINCRONIZADO con `BEGINNER_ALLOWED_RACE_DISTANCES` del
 * backend. Si Alberto amplía el allowlist (e.g. añadir `"trail_short"`),
 * actualizar AMBOS sitios.
 */

import type { AthleteLevel } from "@/types/api";

/** Allowlist case-insensitive (lowercase). Único valor permitido: "5k". */
const BEGINNER_ALLOWED_RACE_DISTANCES: ReadonlySet<string> = new Set(["5k"]);

/**
 * Filtra una lista de distancias según el nivel del atleta.
 *
 * - `level === "beginner"`: devuelve solo las distancias en el allowlist
 *   (matching case-insensitive — cubre la inconsistencia histórica
 *   "5K" mayúscula vs "5k" minúscula entre selectores).
 * - `level === "amateur"` o cualquier otro valor (null, undefined,
 *   legacy): devuelve la lista completa sin filtrar — amateur sin
 *   restricción §8.4, y perfiles incompletos no se penalizan en UI
 *   (otros gates los manejan).
 *
 * @param level - nivel del atleta (`AthleteLevel | null`).
 * @param allDistances - lista completa de valores de distancia que
 *   ofrecería el selector si no hubiera restricción.
 * @returns lista filtrada (puede ser la misma referencia si no se filtra).
 */
export function getAllowedRaceDistances<T extends string>(
  level: AthleteLevel | null | undefined,
  allDistances: ReadonlyArray<T>,
): T[] {
  if (level !== "beginner") {
    return [...allDistances];
  }
  return allDistances.filter((d) =>
    BEGINNER_ALLOWED_RACE_DISTANCES.has(d.toLowerCase()),
  );
}
